import { useState, useEffect } from 'react';
import { 
  Calculator, Plus, Trash2, Save, RotateCcw, RefreshCw, Loader2,
  Target, MapPin, Briefcase, Users, MessageSquare, CheckCircle,
  ChevronDown, ChevronUp, Info, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'source', label: 'Lead Source', icon: Users, color: 'bg-blue-500' },
  { value: 'service', label: 'Service Interest', icon: Briefcase, color: 'bg-orange-500' },
  { value: 'location', label: 'Location', icon: MapPin, color: 'bg-green-500' },
  { value: 'engagement', label: 'Engagement', icon: MessageSquare, color: 'bg-purple-500' },
  { value: 'timing', label: 'Timing', icon: Target, color: 'bg-pink-500' },
];

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in_list', label: 'Is in list (comma-separated)' },
  { value: 'not_empty', label: 'Is not empty' },
  { value: 'is_empty', label: 'Is empty' },
];

const CONDITION_FIELDS = [
  { value: 'source', label: 'Source (appointment, contact, csv_import)' },
  { value: 'service_needed', label: 'Service Needed' },
  { value: 'city', label: 'City' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'message', label: 'Message/Notes' },
  { value: 'email', label: 'Email' },
  { value: 'name', label: 'Name' },
];

export default function AdminLeadScoring() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['source', 'service', 'location']);
  
  const [config, setConfig] = useState({
    base_score: 50,
    max_score: 100,
    min_score: 0,
    rules: []
  });
  
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    category: 'source',
    condition_field: 'source',
    condition_operator: 'equals',
    condition_value: '',
    points: 10,
    is_active: true,
    priority: 5
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const data = await apiRequest('/admin/lead-scoring');
      setConfig(data);
    } catch (error) {
      toast.error('Failed to load scoring configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await apiRequest('/admin/lead-scoring', {
        method: 'PUT',
        body: JSON.stringify(config)
      });
      toast.success('Scoring configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const addRule = async () => {
    if (!newRule.name) {
      toast.error('Rule name is required');
      return;
    }
    
    try {
      const rule = await apiRequest('/admin/lead-scoring/rules', {
        method: 'POST',
        body: JSON.stringify(newRule)
      });
      setConfig(prev => ({
        ...prev,
        rules: [...prev.rules, rule]
      }));
      setShowAddRule(false);
      setNewRule({
        name: '',
        description: '',
        category: 'source',
        condition_field: 'source',
        condition_operator: 'equals',
        condition_value: '',
        points: 10,
        is_active: true,
        priority: 5
      });
      toast.success('Rule added');
    } catch (error) {
      toast.error('Failed to add rule');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await apiRequest(`/admin/lead-scoring/rules/${ruleId}`, {
        method: 'DELETE'
      });
      setConfig(prev => ({
        ...prev,
        rules: prev.rules.filter(r => r.id !== ruleId)
      }));
      toast.success('Rule deleted');
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRule = (ruleId) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(r => 
        r.id === ruleId ? { ...r, is_active: !r.is_active } : r
      )
    }));
  };

  const updateRulePoints = (ruleId, points) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(r => 
        r.id === ruleId ? { ...r, points: parseInt(points) || 0 } : r
      )
    }));
  };

  const recalculateScores = async () => {
    setRecalculating(true);
    try {
      const result = await apiRequest('/admin/lead-scoring/recalculate', {
        method: 'POST'
      });
      toast.success(`Updated ${result.updated} of ${result.total} leads`);
    } catch (error) {
      toast.error('Failed to recalculate scores');
    } finally {
      setRecalculating(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset all scoring rules to defaults? This cannot be undone.')) {
      return;
    }
    
    try {
      const data = await apiRequest('/admin/lead-scoring/reset', {
        method: 'POST'
      });
      setConfig(data);
      toast.success('Reset to default rules');
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getRulesByCategory = (category) => {
    return config.rules.filter(r => r.category === category);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : Target;
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.color : 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-lead-scoring" className="pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Lead Scoring
          </h1>
          <p className="text-slate-500 mt-1">Configure how leads are scored based on their attributes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Defaults
          </Button>
          <Button 
            onClick={recalculateScores} 
            variant="outline"
            disabled={recalculating}
          >
            {recalculating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Recalculate All
          </Button>
          <Button onClick={saveConfig} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Score Settings */}
      <Card className="border-none shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="w-5 h-5 text-orange-600" />
            Score Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="base_score">Base Score</Label>
              <Input
                id="base_score"
                type="number"
                value={config.base_score}
                onChange={(e) => setConfig(prev => ({ ...prev, base_score: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Starting score for all leads</p>
            </div>
            <div>
              <Label htmlFor="min_score">Minimum Score</Label>
              <Input
                id="min_score"
                type="number"
                value={config.min_score}
                onChange={(e) => setConfig(prev => ({ ...prev, min_score: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Scores can't go below this</p>
            </div>
            <div>
              <Label htmlFor="max_score">Maximum Score</Label>
              <Input
                id="max_score"
                type="number"
                value={config.max_score}
                onChange={(e) => setConfig(prev => ({ ...prev, max_score: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Scores can't exceed this</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How Scoring Works */}
      <Card className="border-none shadow-sm mb-6 bg-blue-50 border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">How Lead Scoring Works</p>
              <p className="text-sm text-blue-800 mt-1">
                Each lead starts with the <strong>base score ({config.base_score})</strong>. 
                Rules are evaluated based on lead attributes, adding or subtracting points. 
                Higher priority rules are evaluated first. Final score is clamped between {config.min_score} and {config.max_score}.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Rule Button */}
      <div className="mb-6">
        <Button onClick={() => setShowAddRule(true)} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Add New Rule
        </Button>
      </div>

      {/* Rules by Category */}
      <div className="space-y-4">
        {CATEGORIES.map((category) => {
          const rules = getRulesByCategory(category.value);
          const isExpanded = expandedCategories.includes(category.value);
          const activeRules = rules.filter(r => r.is_active).length;
          const CategoryIcon = category.icon;
          
          return (
            <Card key={category.value} className="border-none shadow-sm overflow-hidden">
              <CardHeader 
                className={`${category.color} text-white cursor-pointer`}
                onClick={() => toggleCategory(category.value)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CategoryIcon className="w-5 h-5" />
                    <span>{category.label}</span>
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      {activeRules}/{rules.length} active
                    </Badge>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </CardTitle>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="p-4">
                  {rules.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No rules in this category</p>
                  ) : (
                    <div className="space-y-3">
                      {rules.map((rule) => (
                        <div 
                          key={rule.id}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            rule.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'
                          }`}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <Switch
                              checked={rule.is_active}
                              onCheckedChange={() => toggleRule(rule.id)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`font-medium ${rule.is_active ? 'text-slate-900' : 'text-slate-400'}`}>
                                  {rule.name}
                                </p>
                                {rule.is_active && (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{rule.description}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {rule.condition_field} {rule.condition_operator} "{rule.condition_value}"
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm text-slate-600">Points:</Label>
                              <Input
                                type="number"
                                value={rule.points}
                                onChange={(e) => updateRulePoints(rule.id, e.target.value)}
                                className="w-20 text-center"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRule(rule.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Rule Dialog */}
      <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Scoring Rule</DialogTitle>
            <DialogDescription>
              Create a new rule to adjust lead scores based on their attributes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="rule_name">Rule Name *</Label>
              <Input
                id="rule_name"
                value={newRule.name}
                onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., High-Value Service"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="rule_desc">Description</Label>
              <Input
                id="rule_desc"
                value={newRule.description}
                onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Interested in premium services"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={newRule.category} 
                  onValueChange={(v) => setNewRule(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Field to Check</Label>
                <Select 
                  value={newRule.condition_field} 
                  onValueChange={(v) => setNewRule(prev => ({ ...prev, condition_field: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_FIELDS.map(field => (
                      <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Operator</Label>
                <Select 
                  value={newRule.condition_operator} 
                  onValueChange={(v) => setNewRule(prev => ({ ...prev, condition_operator: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATORS.map(op => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Value</Label>
                <Input
                  value={newRule.condition_value}
                  onChange={(e) => setNewRule(prev => ({ ...prev, condition_value: e.target.value }))}
                  placeholder="e.g., Full Dentures"
                  className="mt-1"
                  disabled={['not_empty', 'is_empty'].includes(newRule.condition_operator)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Points (+ or -)</Label>
                <Input
                  type="number"
                  value={newRule.points}
                  onChange={(e) => setNewRule(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Priority (higher = first)</Label>
                <Input
                  type="number"
                  value={newRule.priority}
                  onChange={(e) => setNewRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={newRule.is_active}
                onCheckedChange={(checked) => setNewRule(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Rule is active</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddRule(false)}>
              Cancel
            </Button>
            <Button onClick={addRule} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
